import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ClaimKey } from '../../../../models/api/core/claim-key';
import {
  FinancialRecoveriesResponse,
  Receivables,
  Recovery,
  Subrogation,
} from '../../../../models/api/view/claim/financials/recoveries/financial-recoveries';
import { Action } from '../../../../models/core/action';
import { BaseComponent } from '../../../base/base.component';
import { SelectComponent } from '../../../core/controls/select.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { CardComponent } from '../../../core/panels/card.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';

@Component({
  selector: 'app-recoveries',
  imports: [
    CommonModule,
    DetailPanelComponent,
    DetailItemComponent,
    TableComponent,
    ColumnComponent,
    PageHeaderComponent,
    SelectComponent,
    CardComponent,
  ],
  template: `
    @if (recoveriesSection) {
      <app-page-header label="Recoveries">
        <ng-template #right>
          <div class="tw-flex tw-items-center tw-w-64">
            <div class="tw-grow">
              <app-select
                [options]="levels"
                optionLabel="label"
                optionValue="key"
                [virtualScroll]="false"
                [control]="level"
                label="View"
                [multiple]="false"
                [filter]="false"
                [showClear]="false"
                [showHelpText]="false"
              />
            </div>
          </div>
        </ng-template>
      </app-page-header>

      <div
        class="tw-flex tw-flex-col sm:tw-flex-row"
        [ngClass]="{
          'tw-gap-4': !disability,
          'tw-gap-8': disability,
        }"
      >
        @if (!disability) {
          <div class="tw-basis-full sm:tw-basis-1/3 tw-min-w-0">
            <app-card label="Receivables">
              <app-detail-panel [dataSource]="receivables">
                <app-detail-item label="Paid" name="paid" format="c" />
                <app-detail-item label="Expected" name="expected" format="c" />
              </app-detail-panel>
            </app-card>
          </div>
        }

        <div class="tw-basis-full sm:tw-basis-2/3 tw-min-w-0">
          <app-card label="Recoveries">
            <app-table
              [dataSource]="recoveries"
              [pageSize]="5"
              [scrollable]="false"
              dataKey="id"
              recordLabel="Recovery"
              recordLabelPlural="Recoveries"
            >
              <app-column name="type" label="Recovery type">
                <ng-template let-data>
                  <b>{{ data.type }}</b>
                </ng-template>
              </app-column>
              @if (!disability) {
                <app-column
                  name="indemnity"
                  label="Indemnity/Loss"
                  format="c"
                />
                <app-column name="medical" label="Medical" format="c" />
                <app-column name="expense" label="Expense" format="c" />
                <app-column name="other" label="Other" format="c" />
              }
              <app-column name="total" [label]="totalLabel" format="c" />
            </app-table>
          </app-card>
        </div>
      </div>
    }
    @if (disability && subrogationSection) {
      <div class="tw-pt-8">
        <app-page-header label="Subrogation"> </app-page-header>
      </div>
      <div class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-4">
        <div class="tw-basis-full sm:tw-basis-1/3 tw-min-w-0">
          <app-card label="Lien notices sent">
            <app-detail-panel [dataSource]="subrogation">
              <app-detail-item
                label="Third party"
                name="thirdPartyDate"
                format="d"
              />
              <app-detail-item label="Insurer" name="insurerDate" format="d" />
              <app-detail-item
                label="Attorney"
                name="attorneyDate"
                format="d"
              />
              <app-detail-item
                label="Statute of limitations expires"
                name="limitationExpirationDate"
                format="d"
              />
            </app-detail-panel>
          </app-card>
        </div>
      </div>
    }
    @if (!disability && subrogationSection) {
      <app-page-header label="Subrogation" />
      <div class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-8">
        <div class="tw-basis-full sm:tw-basis-2/3 tw-min-w-0">
          <app-card label="Lien notices sent">
            <app-detail-panel [dataSource]="subrogation" class="tw-w-full">
              <app-detail-item
                label="Third party"
                name="thirdPartyDate"
                format="d"
              />
              <app-detail-item label="Insurer" name="insurerDate" format="d" />
              <app-detail-item
                label="Attorney"
                name="attorneyDate"
                format="d"
              />
              <app-detail-item
                label="Statute of limitations expires"
                name="limitationExpirationDate"
                format="d"
              />
            </app-detail-panel>
          </app-card>
        </div>

        <div class="tw-basis-full sm:tw-basis-1/2 tw-min-w-0">
          <app-card label="Special funds, excess, carrier reporting">
            <app-detail-panel [dataSource]="subrogation" class="tw-w-full">
              <app-detail-item
                label="Notice due date"
                name="noticeDueDate"
                format="d"
              />
              <app-detail-item
                label="Notice filed"
                name="noticeFiled"
                format="d"
              />
              <app-detail-item
                label="Date accepted by fund"
                name="dateAcceptedByFund"
                format="d"
              />
              <app-detail-item
                label="Claim in excess"
                name="claimInExcess"
                format="b"
              />
              <app-detail-item
                label="Notice sent"
                name="noticeSent"
                format="d"
              />
              <app-detail-item
                label="Claim in excess but decontrolled by carrier"
                name="carrierAcceptedStatus"
                format="b"
              />
              <app-detail-item
                label="SIF reimbursement due"
                labelCase="none"
                name="sifReimbDueDate"
                format="d"
              />
              <app-detail-item
                label="SIF reimbursement filed"
                labelCase="none"
                name="sifReimbFiledDate"
                format="d"
              />
              <app-detail-item
                label="SIF terminated"
                labelCase="none"
                name="sifTerminated"
              />
              <app-detail-item
                label="Claim reported to carrier"
                name="claimReportedToDate"
                format="d"
              />
              <app-detail-item
                label="Claim decontrolled by carrier"
                name="claimDecontrolledByCarrierDate"
                format="d"
              />
            </app-detail-panel>
          </app-card>
        </div>
      </div>
    }
    @if (workersComp && subrogationSection) {
      <app-page-header label="Type of recovery" />
      <div class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-8">
        <div class="tw-basis-full sm:tw-basis-1/3 tw-min-w-0">
          <app-detail-panel [dataSource]="recoveriesResponse" class="tw-w-full">
            <app-detail-item label="Type of recovery" name="recoveryType" />
            <app-detail-item
              label="Carrier gross total incurred"
              name="carrierGrossTotalIncurred"
              format="c"
            />
          </app-detail-panel>
        </div>
      </div>
    }
  `,
  styles: ``,
})
export class RecoveriesComponent extends BaseComponent implements OnInit {
  level = new FormControl<string>('Claim');
  disability = false;
  workersComp = false;
  au = false;
  gl = false;
  pr = false;
  liability = false;
  totalLabel = 'Total';

  levels: Action[] = [
    { key: 'Claim', label: 'Claim' },
    { key: 'Claimant', label: 'Claimant' },
    { key: 'Event', label: 'Event' },
  ];

  data = {};
  items: any[] = [];

  claim = this.view.currentClaim();
  recoveriesResponse: FinancialRecoveriesResponse;
  recoveries: Recovery[] = [];
  subrogation: Subrogation;
  receivables: Receivables;

  // Security token flags for section visibility
  recoveriesSection = false;
  subrogationSection = false;

  async ngOnInit() {
    this.disability = this.claim.lineOfBusiness === 'DS';
    this.workersComp = this.claim.lineOfBusiness === 'WC';
    this.au = this.claim.lineOfBusiness === 'AU';
    this.gl = this.claim.lineOfBusiness === 'GL';
    this.pr = this.claim.lineOfBusiness === 'PR';
    this.liability = this.au || this.gl || this.pr;
    this.totalLabel = 'Amount';

    // Check security tokens for section visibility
    this.recoveriesSection = this.session.authorize('Recoveries');

    // For MT claims, Subrogation and Type of Recovery sections are suppressed
    if (this.claim?.isMTClaim) {
      this.subrogationSection = false;
    } else {
      this.subrogationSection = this.session.authorize('Subrogation');
    }

    const needsRecoveriesEndpoint =
      this.recoveriesSection || this.subrogationSection;

    if (this.disability) {
      await this.loadDisabilityData(needsRecoveriesEndpoint);
      this.updateViewTypeData(this.level.value);

      this.dispose.subs = this.level.valueChanges.subscribe((value) => {
        this.updateViewTypeData(value);
      });
    } else {
      const claimKey: ClaimKey = {
        recordId: this.claim.recordId,
        recordSource: this.claim.recordSource,
      };
      this.recoveriesResponse = needsRecoveriesEndpoint
        ? await this.api.recoveries.get(claimKey)
        : {
            receivables: [],
            recoveries: [],
            subrogation: {} as Subrogation,
          };
      this.subrogation = this.recoveriesResponse.subrogation;
      this.processSifTerminated();
      this.updateViewTypeData(this.level.value);

      this.dispose.subs = this.level.valueChanges.subscribe((value) => {
        this.updateViewTypeData(value);
      });
    }
    this.view.hideLoading();
  }

  private async loadDisabilityData(needsRecoveriesEndpoint: boolean) {
    const claimKey: ClaimKey = {
      recordId: this.claim.recordId,
      recordSource: this.claim.recordSource,
    };
    this.recoveriesResponse = needsRecoveriesEndpoint
      ? await this.api.recoveries.get(claimKey)
      : {
          receivables: [],
          recoveries: [],
          subrogation: {} as Subrogation,
        };
    this.subrogation = this.recoveriesResponse.subrogation;
    this.processSifTerminated();
  }

  private processSifTerminated() {
    if (this.subrogation?.sifTerminated) {
      const value = this.subrogation.sifTerminated.trim();
      if (value.toLowerCase() === 'no' || value === '') {
        this.subrogation.sifTerminated = '';
      } else {
        this.subrogation.sifTerminated = value;
      }
    } else {
      this.subrogation.sifTerminated = '';
    }
  }

  private updateViewTypeData(viewType: string | null) {
    const vt = viewType ?? 'Claim';
    const rec = this.recoveriesResponse.receivables.find(
      (r) => r.viewType === vt,
    );
    this.receivables = rec ?? { viewType: vt, paid: 0, expected: 0 };
    this.recoveries = this.recoveriesResponse.recoveries.filter(
      (r) => r.viewType === vt,
    );
  }
}
